// Deterministic dagre layout for the Solution Studio diagram editor (§8).
//
// The generated Flow DSL carries no node positions (the `flows` analyze slice never emits them), so
// DiagramEditor would otherwise stack every node on the origin. This is a synchronous, render-
// independent layout: we estimate per-node sizes (no DOM measurement — from the node's content +
// branch handles) and run dagre top-to-bottom, then a light collision sweep nudges any residual overlap.
//
// This intentionally does NOT reuse diagrams/utils/computeFlowLayout.getLayoutedElements — that one is
// async and coupled to the viewer's custom node DOM (NodeMeasurement) and to an `entry-node`. We mirror
// its dagre parameters + collision-resolution idea (kept synchronous + DOM-free) so the arrangement
// looks alike without the heavyweight measurement pipeline.

import dagre from 'dagre';

// Base card footprint (px). The custom flowNode renders at a fixed width; height grows with the
// description line, the key-params rows, and the rotated branch-label gutter under branching nodes.
const NODE_WIDTH = 228;
const BASE_HEIGHT = 64;

/**
 * Estimate a node's laid-out footprint from its rendered content. Width grows with the number of
 * LABELLED branch handles (the fanned-out exit labels need horizontal room so siblings don't collide);
 * height grows with the description line, key-param rows, and a gutter for the rotated branch labels.
 */
function estimateSize(node) {
  const data = node?.data || {};
  const labeledBranches = (data.branches || []).filter((b) => b && b.label).length;
  const detailRows = (data.detail || []).length;
  const width = NODE_WIDTH + Math.max(0, labeledBranches - 1) * 36;
  const height = BASE_HEIGHT + (data.description ? 14 : 0) + detailRows * 16 + (labeledBranches ? 44 : 0);
  return { width, height };
}

/**
 * Lay out React Flow nodes top-to-bottom with dagre, then resolve any residual overlap. Pure: returns
 * NEW node objects with `position` (and source/target positions for TB) — never mutates the inputs.
 * Falls back to a simple grid if dagre throws (e.g. a malformed graph) so the editor always renders.
 */
export function layoutNodes(nodes, edges) {
  if (!Array.isArray(nodes) || nodes.length === 0) return nodes || [];
  const safeEdges = Array.isArray(edges) ? edges : [];
  const ids = new Set(nodes.map((n) => n.id));

  try {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', nodesep: 90, ranksep: 130, marginx: 40, marginy: 40 });

    const sizeById = new Map();
    nodes.forEach((n) => { const s = estimateSize(n); sizeById.set(n.id, s); g.setNode(n.id, s); });
    safeEdges.forEach((e) => {
      if (ids.has(e.source) && ids.has(e.target)) g.setEdge(e.source, e.target);
    });

    dagre.layout(g);

    const laid = nodes.map((n) => {
      const pos = g.node(n.id);
      if (!pos) return n;
      return {
        ...n,
        position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 },
        targetPosition: 'top',
        sourcePosition: 'bottom',
      };
    });
    return resolveCollisions(laid, sizeById);
  } catch {
    return gridFallback(nodes);
  }
}

/**
 * Synchronous AABB push-apart (ported from diagrams/utils/computeFlowLayout's collision resolver, kept
 * DOM-free). Nudges overlapping nodes apart along their smaller-overlap axis with a fixed buffer. Only
 * mutates the freshly-built position objects from layoutNodes (never shared with the caller's inputs).
 */
function resolveCollisions(nodes, sizeById) {
  const BUF = 26;
  const MAX_ITER = 6;
  const items = nodes
    .filter((n) => n.position && sizeById.has(n.id))
    .map((n) => ({ n, ...sizeById.get(n.id) }));
  for (let iter = 0; iter < MAX_ITER; iter++) {
    let moved = false;
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i]; const b = items[j];
        const aL = a.n.position.x; const aR = aL + a.width; const aT = a.n.position.y; const aB = aT + a.height;
        const bL = b.n.position.x; const bR = bL + b.width; const bT = b.n.position.y; const bB = bT + b.height;
        const overlapped = !(aR + BUF <= bL || bR + BUF <= aL || aB + BUF <= bT || bB + BUF <= aT);
        if (!overlapped) continue;
        const ox = Math.min(aR, bR) - Math.max(aL, bL) + BUF;
        const oy = Math.min(aB, bB) - Math.max(aT, bT) + BUF;
        if (ox <= oy) {
          const dir = (aL + aR) / 2 <= (bL + bR) / 2 ? -1 : 1;
          a.n.position.x += dir * (ox / 2);
          b.n.position.x -= dir * (ox / 2);
        } else {
          const dir = (aT + aB) / 2 <= (bT + bB) / 2 ? -1 : 1;
          a.n.position.y += dir * (oy / 2);
          b.n.position.y -= dir * (oy / 2);
        }
        moved = true;
      }
    }
    if (!moved) break;
  }
  return nodes;
}

function gridFallback(nodes) {
  const COLS = 4;
  return nodes.map((n, i) => ({
    ...n,
    position: { x: (i % COLS) * (NODE_WIDTH + 60), y: Math.floor(i / COLS) * (BASE_HEIGHT + 100) },
    targetPosition: 'top',
    sourcePosition: 'bottom',
  }));
}

/**
 * True when the node set has no usable layout — every node is missing a position or they are all
 * identical (e.g. the generated DSL's implicit {0,0}). Used to decide whether to auto-arrange on open.
 */
export function needsLayout(nodes) {
  if (!Array.isArray(nodes) || nodes.length <= 1) return false;
  const seen = new Set();
  for (const n of nodes) {
    const p = n.position;
    if (!p || (p.x == null && p.y == null)) return true;
    seen.add(`${p.x},${p.y}`);
  }
  return seen.size <= 1;
}
